import {
    PublicClientApplication,
    Configuration,
    AccountInfo,
    AuthenticationResult,
    InteractionRequiredAuthError,
} from "@azure/msal-browser";

// Explicit sessionStorage cache per user preference (no localStorage).
const msalConfig: Configuration = {
    auth: {
        clientId: import.meta.env.VITE_CLIENT_ID || "",
        authority: `https://login.microsoftonline.com/${import.meta.env.VITE_TENANT_ID}`,
        redirectUri:
            typeof window !== "undefined" ? window.location.origin : "/",
        // Use root as the default post logout landing to avoid re-entering /logout and triggering another auth cycle.
        postLogoutRedirectUri: "/logout",
    },
    cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: true,
    },
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Legacy account export for backward compatibility
export let account: AccountInfo | null = null;
export const setAccount = (acc: AccountInfo | null) => {
    account = acc;
};

// Readiness synchronization so lower layers (axios) can wait for initial redirect processing.
let ready = false;
let readyResolve: (() => void) | null = null;
export const authReady: Promise<void> = new Promise(
    res =>
        (readyResolve = () => {
            ready = true;
            res();
        }),
);

export function markAuthReady() {
    if (!ready && readyResolve) readyResolve();
}

let initPromise: Promise<void> | null = null;
let initialized = false; // Our full auth initialization (redirect processed + account set)
let sdkInitialized = false; // Underlying MSAL SDK initialize() call
export const LOGIN_FLAG = "msal_login_in_progress";

export function hasAuthResponseInUrl(): boolean {
    const { search, hash } = window.location;
    return /(code=|error=)/.test(search) || /(code=|error=)/.test(hash);
}

export function getActiveAccount(): AccountInfo | null {
    return msalInstance.getActiveAccount();
}

export async function initAuth(): Promise<void> {
    if (initialized) {
        return;
    }
    if (initPromise) {
        return initPromise;
    }
    initPromise = (async () => {
        try {
            if (!sdkInitialized) {
                await msalInstance.initialize();
                sdkInitialized = true;
            }
            const result = await msalInstance.handleRedirectPromise();
            if (result?.account) {
                msalInstance.setActiveAccount(result.account);
                account = result.account;
            } else {
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    msalInstance.setActiveAccount(accounts[0]);
                    account = accounts[0];
                }
            }
        } finally {
            initialized = true;
            sessionStorage.removeItem(LOGIN_FLAG);
            markAuthReady();
        }
    })();
    return initPromise;
}

export function ensureLoggedIn(scopes: string[]) {
    if (getActiveAccount()) return;
    // Ensure underlying SDK initialized before triggering interactive flow (avoid race with initAuth in rare cases)
    if (!sdkInitialized) {
        // Fire and forget; interactive call itself will wait until ready internally
        msalInstance.initialize().then(() => {
            sdkInitialized = true;
        });
    }
    if (hasAuthResponseInUrl()) return; // processing redirect
    if (sessionStorage.getItem(LOGIN_FLAG)) return; // already initiating
    sessionStorage.setItem(LOGIN_FLAG, "1");
    msalInstance.loginRedirect({ scopes });
}

export async function acquireTokenSilently(
    scopes: string[],
): Promise<{ token: string | null; kind: "access" | "id" | null }> {
    await initAuth();
    const account = getActiveAccount();
    if (!account) {
        ensureLoggedIn(scopes);
        return { token: null, kind: null };
    }
    try {
        const result = await msalInstance.acquireTokenSilent({
            scopes,
            account,
        });
        // We purposely return idToken (legacy backend expectation) even if an access token exists.
        if (result.idToken) return { token: result.idToken, kind: "id" };
        if (result.accessToken)
            return { token: result.accessToken, kind: "access" };
        return { token: null, kind: null };
    } catch (e: any) {
        if (
            e instanceof InteractionRequiredAuthError ||
            [
                "interaction_required",
                "login_required",
                "consent_required",
            ].includes(e.errorCode)
        ) {
            ensureLoggedIn(scopes);
            return { token: null, kind: null };
        }
        throw e;
    }
}
